import React from "react"
import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"
import CircularProgress from "@material-ui/core/CircularProgress"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import Typography from "@material-ui/core/Typography"
import { makeStyles } from "@material-ui/core"
import Alert from "@material-ui/lab/Alert"
import { Mail } from "react-feather"
import { useTranslation } from "react-i18next"
import { errorToMessage } from "../../../utils/errors"
import { useHourReportTranslations } from "../../hooks/use-hour-report-translations"

const defaultTranslations = {
  title: "Mark ready for approval?",
  description:
    "This will mark all selected days and their time entries as ready for approval. You can also notify the approver by email.",
  error: "Total time could not be marked ready for approval.",
  confirmAndNotify: "Mark ready for approval and notify approver",
  confirmWithoutNotify: "Mark ready for approval without notifying",
}

type TotalReadyForApprovalDialogProps = {
  request: TotalReadyForApprovalDialogRequest | null
  onClose: () => void
  onCheck: (notifyApprover: boolean) => Promise<boolean | void>
  requestState: {
    isLoading: boolean
    isError: boolean
    error?: unknown
  }
}

export type TotalReadyForApprovalDialogRequest = {
  days: string[]
  reportCount: number
}

const TotalReadyForApprovalDialog = ({ request, onClose, onCheck, requestState }: TotalReadyForApprovalDialogProps) => {
  const classes = useStyles()
  const { t: typedT } = useTranslation("hour-report")
  const t = typedT as unknown as (key: string, options: Record<string, unknown>) => string
  const { dialogs } = useHourReportTranslations()
  const [hasSubmitted, setHasSubmitted] = React.useState(false)

  React.useEffect(() => {
    setHasSubmitted(false)
  }, [request])

  if (!request) return null

  const isError = hasSubmitted && requestState.isError

  const handleCheck = (notifyApprover: boolean) => {
    setHasSubmitted(true)
    onCheck(notifyApprover)
  }

  const handleDialogClose = (_event: unknown, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return
    onClose()
  }

  return (
    <Dialog
      open
      onClose={handleDialogClose}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      aria-labelledby="total-ready-for-approval-dialog-title"
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle id="total-ready-for-approval-dialog-title">
        {t("gridDialogs.totalReadyForApprovalTitle", { defaultValue: defaultTranslations.title })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="textSecondary">
          {t("gridDialogs.totalReadyForApprovalDescription", { defaultValue: defaultTranslations.description })}
        </Typography>
        <Box className={classes.summary}>
          <Box className={classes.summaryItem}>
            <Typography variant="caption" color="textSecondary">
              {dialogs.days}
            </Typography>
            <Typography variant="h6">{request.days.length}</Typography>
          </Box>
          <Box className={classes.summaryDivider} />
          <Box className={classes.summaryItem}>
            <Typography variant="caption" color="textSecondary">
              {dialogs.reports}
            </Typography>
            <Typography variant="h6">{request.reportCount}</Typography>
          </Box>
        </Box>
        {isError ? (
          <Alert variant="filled" severity="error" className={classes.feedbackAlert}>
            {errorToMessage(
              requestState.error,
              t("gridDialogs.totalReadyForApprovalError", { defaultValue: defaultTranslations.error })
            )}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={() => handleCheck(true)}
          color="primary"
          variant="contained"
          disabled={requestState.isLoading}
          fullWidth
          startIcon={requestState.isLoading ? <CircularProgress size={16} color="inherit" /> : <Mail size={16} />}
        >
          {t("gridDialogs.totalReadyForApprovalAndNotify", { defaultValue: defaultTranslations.confirmAndNotify })}
        </Button>
        <Box className={classes.secondaryActions}>
          <Button onClick={() => handleCheck(false)} variant="outlined" disabled={requestState.isLoading} fullWidth>
            {t("gridDialogs.totalReadyForApprovalWithoutNotify", {
              defaultValue: defaultTranslations.confirmWithoutNotify,
            })}
          </Button>
          <Button onClick={onClose} variant="outlined" disabled={requestState.isLoading} fullWidth>
            {dialogs.cancel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: 544,
  },
  summary: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.grey["100"],
  },
  summaryItem: {
    minWidth: 0,
  },
  summaryDivider: {
    width: 1,
    height: 40,
    margin: theme.spacing(0, 2),
    backgroundColor: theme.palette.divider,
  },
  feedbackAlert: {
    marginTop: theme.spacing(2),
  },
  actions: {
    display: "block",
    padding: theme.spacing(2, 3, 3),
    "& *:not(:first-child)": {
      marginLeft: 0,
    },
  },
  secondaryActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    "& button": {
      color: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
    },
  },
}))

export default TotalReadyForApprovalDialog
